import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase-auth'
import {
  XMarkIcon,
  BuildingOfficeIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

interface EditPartyModalProps {
  isOpen: boolean
  party: any
  onClose: () => void
  onSuccess: () => void
}

export const EditPartyModal: React.FC<EditPartyModalProps> = ({
  isOpen,
  party,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [advisors, setAdvisors] = useState<Array<{ id: string; full_name: string }>>([])

  const [formData, setFormData] = useState({
    name: '',
    type: 'individual' as 'individual' | 'organization',
    taxId: '',
    primaryEmail: '',
    primaryPhone: '',
    assignedAdvisorId: '',
    status: 'active' as 'active' | 'inactive' | 'archived',
    notes: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen && party) {
      fetchAdvisors()
      // Populate form with party data
      setFormData({
        name: party.name || party.full_name || '',
        type: party.type || 'individual',
        taxId: party.tax_id || '',
        primaryEmail: party.primary_email || '',
        primaryPhone: party.primary_phone || '',
        assignedAdvisorId: party.assigned_advisor_id || '',
        status: party.status || 'active',
        notes: party.notes || ''
      })
      setErrors({})
    }
  }, [isOpen, party])

  const fetchAdvisors = async () => {
    try {
      const { data, error } = await supabase
        .from('platform_users')
        .select('id, full_name')
        .in('role', ['member', 'admin', 'owner'])
        .order('full_name')

      if (error) throw error
      setAdvisors(data || [])
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching advisors:', error)
      }
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (formData.primaryEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primaryEmail)) {
      newErrors.primaryEmail = 'Please enter a valid email address'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setLoading(true)

    try {
      const { error } = await supabase
        .from('parties')
        .update({
          name: formData.name,
          type: formData.type,
          tax_id: formData.taxId || null,
          primary_email: formData.primaryEmail || null,
          primary_phone: formData.primaryPhone || null,
          assigned_advisor_id: formData.assignedAdvisorId || null,
          status: formData.status,
          notes: formData.notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', party.id)

      if (error) throw error

      toast.success('Client updated successfully!')
      onSuccess()
      onClose()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating client:', error)
      }
      toast.error('Failed to update client')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Edit Client</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Client Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Type
              </label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, type: 'individual' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                    formData.type === 'individual'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <UserIcon className="h-5 w-5" />
                  Individual
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, type: 'organization' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                    formData.type === 'organization'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <BuildingOfficeIcon className="h-5 w-5" />
                  Organization
                </button>
              </div>
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                {formData.type === 'individual' ? 'Full Name' : 'Organization Name'}{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full rounded-md px-3 py-2 bg-white text-gray-900 border ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={formData.primaryEmail}
                onChange={(e) => setFormData({ ...formData, primaryEmail: e.target.value })}
                className={`w-full rounded-md px-3 py-2 bg-white text-gray-900 border ${
                  errors.primaryEmail ? 'border-red-300' : 'border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.primaryEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.primaryEmail}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.primaryPhone}
                onChange={(e) => setFormData({ ...formData, primaryPhone: e.target.value })}
                className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Tax ID */}
            <div>
              <label htmlFor="taxId" className="block text-sm font-medium text-gray-700 mb-1">
                {formData.type === 'individual' ? 'SIN' : 'Business Number'}
              </label>
              <input
                type="text"
                id="taxId"
                value={formData.taxId}
                onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Assigned Advisor */}
            <div>
              <label htmlFor="advisor" className="block text-sm font-medium text-gray-700 mb-1">
                Assigned Advisor
              </label>
              <select
                id="advisor"
                value={formData.assignedAdvisorId}
                onChange={(e) => setFormData({ ...formData, assignedAdvisorId: e.target.value })}
                className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select an advisor</option>
                {advisors.map((advisor) => (
                  <option key={advisor.id} value={advisor.id}>
                    {advisor.full_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Internal notes about this client..."
              />
            </div>
          </form>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditPartyModal
